/**
 * Utility functions for mapping MUI DataGrid server-side params to ABP backend format.
 *
 * MUI DataGrid Premium's `unstable_dataSource` provides `GridGetRowsParams` which
 * must be translated to ABP's `PagedAndSortedResultRequestDto` format.
 */

import type { GridGetRowsParams, GridGetRowsResponse } from '@mui/x-data-grid-premium';

/**
 * ABP paged request parameters
 */
export interface AbpPagedRequest {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  [key: string]: unknown;
}

/**
 * ABP paged response format
 */
export interface AbpPagedResponse<T> {
  items: T[];
  totalCount: number;
}

/**
 * Grouped row returned by the grouped-list endpoint for server-side row grouping.
 */
export interface GroupedRow {
  groupKey: string;
  count: number;
}

/**
 * ABP paged response format for grouped rows.
 */
export interface AbpGroupedResponse {
  items: GroupedRow[];
  totalCount: number;
}

/**
 * Map MUI DataGrid getRows params to ABP PagedAndSortedResultRequestDto format.
 *
 * @param params - MUI GridGetRowsParams from unstable_dataSource
 * @returns ABP-compatible request parameters
 */
export function mapGridParamsToAbp(params: GridGetRowsParams): AbpPagedRequest {
  const { paginationModel, sortModel, filterModel } = params;

  const skipCount = (paginationModel?.page ?? 0) * (paginationModel?.pageSize ?? 25);
  const maxResultCount = paginationModel?.pageSize ?? 25;
  const sorting = buildSortingString(sortModel);
  const filters = buildFilterParams(filterModel);

  return {
    skipCount,
    maxResultCount,
    ...(sorting ? { sorting } : {}),
    ...filters,
  };
}

/**
 * Build ABP sorting string from MUI sort model.
 * ABP expects format: "fieldName asc" or "fieldName desc" or "field1 asc, field2 desc"
 */
export function buildSortingString(
  sortModel: GridGetRowsParams['sortModel'],
): string | undefined {
  if (!sortModel || sortModel.length === 0) return undefined;

  return sortModel
    .map((item) => `${item.field} ${item.sort || 'asc'}`)
    .join(', ');
}

/**
 * Build filter parameters from MUI filter model.
 * Maps common MUI filter operators to ABP query parameters.
 *
 * ABP uses flat query parameters like ?customerName=acme&status=DRAFT
 * rather than nested filter objects.
 */
export function buildFilterParams(
  filterModel: GridGetRowsParams['filterModel'],
): Record<string, unknown> {
  if (!filterModel?.items || filterModel.items.length === 0) return {};

  const params: Record<string, unknown> = {};

  for (const item of filterModel.items) {
    const op = item.operator;

    // isEmpty/isNotEmpty don't require a value — just pass the operator
    if (op === 'isEmpty' || op === 'isNotEmpty') {
      params[`${item.field}Operator`] = op;
      continue;
    }

    if (item.value === undefined || item.value === null || item.value === '') {
      continue;
    }

    // For isAnyOf, the value is an array — serialize as comma-separated string
    // so the backend can split and match against multiple values.
    if (op === 'isAnyOf' && Array.isArray(item.value)) {
      params[item.field] = item.value.join(',');
    } else {
      params[item.field] = item.value;
    }

    // Pass the operator so the backend can apply the correct logic
    // (contains, equals, startsWith, endsWith, =, !=, >, >=, <, <=, etc.)
    if (op) {
      params[`${item.field}Operator`] = op;
    }
  }

  // Quick filter (search across multiple fields)
  if (filterModel.quickFilterValues && filterModel.quickFilterValues.length > 0) {
    params['filter'] = filterModel.quickFilterValues.join(' ');
  }

  return params;
}

/**
 * Convert ABP paged response to MUI GridGetRowsResponse.
 */
export function toGridResponse<T extends { id: string | number }>(
  response: AbpPagedResponse<T>,
): GridGetRowsResponse {
  return {
    rows: response.items,
    rowCount: response.totalCount,
  };
}

/**
 * Convert a GridAggregationModel (from MUI DataGrid Premium) to the
 * "field:fn,field:fn" string format expected by the backend aggregates endpoint.
 *
 * @param aggregationModel - MUI aggregation model: { [field]: 'sum' | 'avg' | 'min' | 'max' }
 * @returns Comma-separated "field:function" string, or undefined if empty
 */
export function buildAggregateFieldsParam(
  aggregationModel: Record<string, string> | undefined,
): string | undefined {
  if (!aggregationModel) return undefined;
  const entries = Object.entries(aggregationModel);
  if (entries.length === 0) return undefined;
  return entries.map(([field, fn]) => `${field}:${fn}`).join(',');
}
